import { Download, Truck } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Badge, Button, formatDateTime, formatMoney, Select, type Column } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { useState } from 'react';
import { KeysetTable } from '../KeysetTable.tsx';
import { useDebouncedValue } from '../useDebouncedValue.ts';
import { canDispatchWith, carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  HANDOVER_ROLE,
  handoverQueryOf,
  isHandoverListingNarrowed,
  useHandovers,
  type Handover,
  type HandoverFilters,
} from './handover.ts';
import { HandoverFilterBar } from './HandoverFilterBar.tsx';
import styles from './Handover.module.css';
import { useVoucherDownload } from './useVoucherDownload.ts';

export type HandoverStartProps = {
  shipping: ShippingClient;
  filters: HandoverFilters;
  onFiltersChange: (filters: HandoverFilters) => void;
  onOpenHandover: (handoverId: string) => void;
};

const SEARCH_DELAY_MS = 300;

export const HandoverStart = ({ shipping, filters, onFiltersChange, onOpenHandover }: HandoverStartProps) => {
  const { t, locale, invalidate, describeError } = usePageContext();
  const canHandOver = useAuth().hasRole(HANDOVER_ROLE);
  const carriers = useCarriers(shipping);
  const [search, setSearch] = useState('');
  const [openedAt] = useState(() => new Date());
  const listing = { ...filters, search: useDebouncedValue(search.trim(), SEARCH_DELAY_MS) };
  const narrowed = isHandoverListingNarrowed(listing);
  const handovers = useHandovers(shipping, handoverQueryOf(listing, openedAt));
  const voucher = useVoucherDownload(shipping);
  const usable = (carriers.data?.items ?? []).filter(canDispatchWith);
  const [chosen, setChosen] = useState<string | null>(null);
  const carrierCode = chosen ?? usable[0]?.code ?? null;
  const carrierName = (code: string) => carrierNameOf(carriers.data?.items, code);
  const open = useMutation({
    mutationFn: (code: string) => shipping.post('/admin/handovers', { body: { carrierCode: code } }),
    onSuccess: async (handover) => {
      await invalidate('handovers');
      onOpenHandover(handover.id);
    },
  });

  const columns: readonly Column<Handover>[] = [
    {
      id: 'carrier',
      header: t('handover.carrier'),
      primary: true,
      cell: (row) => carrierName(row.carrierCode),
    },
    { id: 'openedAt', header: t('handover.openedAt'), cell: (row) => formatDateTime(row.openedAt, locale) },
    {
      id: 'status',
      status: true,
      header: t('handover.status'),
      cell: (row) => (
        <Badge tone={row.status === 'open' ? 'warning' : 'success'}>
          {t(`handover.status_${row.status}`)}
        </Badge>
      ),
    },
    { id: 'count', header: t('handover.count'), numeric: true, cell: (row) => row.parcelCount },
    {
      id: 'total',
      header: t('handover.total'),
      numeric: true,
      cell: (row) =>
        row.codTotal === null ? (
          '—'
        ) : (
          <span className={styles['amount']}>{formatMoney(row.codTotal, locale)}</span>
        ),
    },
    {
      id: 'voucher',
      header: t('handover.voucher'),
      cell: (row) =>
        row.hasVoucher && canHandOver ? (
          <Button
            icon={<Download size={20} aria-hidden="true" />}
            variant="ghost"
            disabled={voucher.isPending}
            onClick={(event) => {
              event.stopPropagation();
              voucher.mutate(row.id);
            }}
          >
            {t('handover.downloadVoucher')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className={styles['panel']}>
      {canHandOver ? (
        <section aria-labelledby="handover-start" className={styles['card']}>
          <h2 id="handover-start">{t('handover.startTitle')}</h2>
          {usable.length === 0 && !carriers.isPending ? (
            <p className={styles['muted']}>{t('handover.noCarrier')}</p>
          ) : (
            <div className={styles['start']}>
              <Select
                label={t('handover.carrier')}
                value={carrierCode ?? ''}
                options={usable.map((carrier) => ({ value: carrier.code, label: carrier.name }))}
                onChange={(event) => {
                  setChosen(event.target.value);
                }}
              />
              <Button
                icon={<Truck size={20} aria-hidden="true" />}
                variant="accent"
                disabled={carrierCode === null || open.isPending}
                onClick={() => {
                  if (carrierCode !== null) open.mutate(carrierCode);
                }}
              >
                {t('handover.start')}
              </Button>
            </div>
          )}
          {open.isError ? <p role="alert">{describeError(open.error)}</p> : null}
        </section>
      ) : null}
      <section aria-labelledby="handover-history" className={styles['panel']}>
        <h2 id="handover-history">{t('handover.history')}</h2>
        <KeysetTable
          pages={handovers}
          columns={columns}
          rowKey={(row) => row.id}
          toolbar={
            <HandoverFilterBar
              shipping={shipping}
              filters={filters}
              search={search}
              onFiltersChange={onFiltersChange}
              onSearchChange={setSearch}
            />
          }
          emptyTitle={narrowed ? t('handover.noMatch') : t('handover.none')}
          emptyDescription={narrowed ? t('handover.noMatchHelp') : t('handover.noneHelp')}
          onRowClick={(row) => {
            onOpenHandover(row.id);
          }}
        />
        {voucher.isError ? <p role="alert">{describeError(voucher.error)}</p> : null}
      </section>
    </div>
  );
};
